"use client";

import { AnimatedBadge } from "@/components/motion/animated-badge";
import { TextRevealRotate } from "@/components/motion/text-reveal-rotate";
import { useViewerLanguage, type Translate } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import {
  AnimatePresence,
  motion,
  useDragControls,
  useReducedMotion,
} from "motion/react";
import { ArrowUp, CornerUpLeft, ThumbsDown, ThumbsUp, X } from "lucide-react";
import * as React from "react";

export type Reaction = 1 | 0 | null;

export interface SheetComment {
  id: string;
  author: string;
  avatarUrl?: string | null;
  time: string;
  text: string;
  isSpeaker?: boolean;
  /** Enables the like affordance and the "Top" sort. No data layer yet. */
  likes?: number;
  liked?: boolean;
  replies?: SheetComment[];
}

export interface CommentSheetLabels {
  title: string;
  count: (n: number) => string;
  emptyBarPhrases: string[];
  emptySheet: string;
  placeholder: string;
  replyPlaceholder: (firstName: string) => string;
  replyingTo: (author: string) => string;
  reply: string;
  sortNew: string;
  sortTop: string;
  speaker: string;
  close: string;
  send: string;
  cancelReply: string;
  helpful: string;
  notHelpful: string;
}

export function commentSheetLabels(t: Translate): CommentSheetLabels {
  return {
    title: t("Comments"),
    count: (n) => t("{count} comments", { count: n }),
    emptyBarPhrases: [t("No comments yet..."), t("Be the first to comment!")],
    emptySheet: t("No comments yet. Be the first to comment!"),
    placeholder: t("Join the discussion..."),
    replyPlaceholder: (firstName) => t("Reply to {name}", { name: firstName }),
    replyingTo: (author) => t("Replying to {name}", { name: author }),
    reply: t("Reply"),
    sortNew: t("Newest"),
    sortTop: t("Top"),
    speaker: t("Speaker"),
    close: t("Close"),
    send: t("Send"),
    cancelReply: t("Cancel reply"),
    helpful: t("Helpful"),
    notHelpful: t("Not helpful"),
  };
}

export interface CommentSheetProps {
  comments: SheetComment[];
  reaction: Reaction;
  /** Optional totals. Without them the thumbs render without numbers. */
  reactionCounts?: { up: number; down: number };
  onReact: (value: 1 | 0) => void;

  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  submitting?: boolean;

  replyTo?: SheetComment;
  onReplyChange: (comment: SheetComment | undefined) => void;

  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Editor mode or missing session: reactions and composer are disabled */
  disabled?: boolean;
  /** Pauses the rotating preview when the slide is off screen */
  isInView?: boolean;
  onToggleLike?: (id: string) => void;
  /** Comment ids whose like is still in flight. */
  pendingLikeIds?: ReadonlySet<string>;
  labels?: Partial<CommentSheetLabels>;
  className?: string;
}

// The handyout slider must not receive scroll/touch events from the section.
const stop = (e: React.SyntheticEvent) => e.stopPropagation();
const stopScroll = {
  onWheel: stop,
  onTouchStart: stop,
  onTouchMove: stop,
  onTouchEnd: stop,
};

// Carried verbatim from comment-input.tsx: the button's pop-in and the field's
// padding shift are one effect and have to settle on the same curve.
const SEND_SPRING = { type: "spring", stiffness: 500, damping: 30 } as const;

// A slow pull clears the distance gate on its own; a flick clears the velocity
// gate after far less travel. Both sit well above tap slop, so a tap on the
// grabber never closes the sheet.
const CLOSE_DRAG_OFFSET = 110;
const CLOSE_FLICK_VELOCITY = 700;
const CLOSE_FLICK_MIN_OFFSET = 24;

// Stiff and critically damped: the panel returns to rest without the default
// overshoot when the viewer has asked for reduced motion.
const SNAP_BACK_REDUCED = { bounceStiffness: 1200, bounceDamping: 70 } as const;

// Pinned to the rest position, then let out downwards only: an upward drag
// cannot move the panel at all, a downward one tracks the finger 1:1.
const CLOSE_DRAG_CONSTRAINTS = { top: 0, bottom: 0 } as const;
const CLOSE_DRAG_ELASTIC = { top: 0, bottom: 1 } as const;

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");

const countAll = (list: SheetComment[]): number =>
  list.reduce((acc, c) => acc + 1 + (c.replies ? countAll(c.replies) : 0), 0);

function Avatar({
  name,
  url,
  size = 24,
  speaker,
}: {
  name: string;
  url?: string | null;
  size?: number;
  speaker?: boolean;
}) {
  if (url) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover shrink-0 ring-1 ring-black/[0.08] dark:ring-white/[0.12]"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full shrink-0 font-medium",
        speaker
          ? "bg-neutral-900 text-neutral-50 dark:bg-neutral-100 dark:text-neutral-900"
          : "bg-neutral-200 dark:bg-neutral-700 ring-1 ring-black/[0.06] dark:ring-white/[0.1] text-neutral-700 dark:text-neutral-200",
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
      aria-hidden
    >
      {initialsOf(name)}
    </span>
  );
}

function SpeakerBadge({ label }: { label: string }) {
  return (
    <AnimatedBadge status="neutral" size="sm" showIcon={false} className="h-5 px-1.5">
      {label}
    </AnimatedBadge>
  );
}

function ReactionPill({
  reaction,
  counts,
  onReact,
  disabled,
  labels,
}: {
  reaction: Reaction;
  counts?: { up: number; down: number };
  onReact: (v: 1 | 0) => void;
  disabled?: boolean;
  labels: CommentSheetLabels;
}) {
  // Zero is suppressed the way the peek bar suppresses its count line: an
  // untouched slide shows bare thumbs rather than "0". Once either side has a
  // vote both numbers appear, so the two segments never go lopsided.
  const showCounts = !!counts && counts.up + counts.down > 0;
  const widest = counts ? String(Math.max(counts.up, counts.down)) : "";

  const btn = (v: 1 | 0) => {
    const active = reaction === v;
    const Icon = v === 1 ? ThumbsUp : ThumbsDown;
    const count = v === 1 ? counts?.up : counts?.down;
    const name = v === 1 ? labels.helpful : labels.notHelpful;
    return (
      <button
        type="button"
        aria-pressed={active}
        aria-label={showCounts ? `${name}, ${count}` : name}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onReact(v);
        }}
        className={cn(
          "flex items-center gap-1.5 rounded-md px-2 py-1 transition-colors disabled:pointer-events-none",
          active
            ? "bg-white dark:bg-neutral-700 shadow-sm text-neutral-900 dark:text-neutral-100"
            : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100",
        )}
      >
        <Icon className={cn("size-3.5", active && "fill-current")} />
        {showCounts && (
          // Both segments reserve the width of the larger count, so the pill
          // stays symmetric and does not jitter as the numbers change.
          <span className="grid text-xs tabular-nums">
            <span aria-hidden className="col-start-1 row-start-1 invisible">
              {widest}
            </span>
            <span className="col-start-1 row-start-1 text-center">{count}</span>
          </span>
        )}
      </button>
    );
  };

  return (
    <div
      className={cn(
        "inline-flex items-center rounded-lg p-0.5 ring-1 ring-black/[0.06] dark:ring-white/[0.08] bg-neutral-100 dark:bg-neutral-900 shrink-0",
        disabled && "opacity-50",
      )}
    >
      {btn(1)}
      <span className="w-px h-4 bg-black/[0.07] dark:bg-white/[0.1]" />
      {btn(0)}
    </div>
  );
}

function CommentItem({
  c,
  compact,
  onReply,
  onToggleLike,
  pendingLikeIds,
  labels,
}: {
  c: SheetComment;
  compact?: boolean;
  onReply?: (c: SheetComment) => void;
  onToggleLike?: (id: string) => void;
  pendingLikeIds?: ReadonlySet<string>;
  labels: CommentSheetLabels;
}) {
  const showLike = !!onToggleLike && typeof c.likes === "number";
  const likePending = !!pendingLikeIds?.has(c.id);
  return (
    <div className="flex gap-2.5">
      <Avatar
        name={c.author}
        url={c.avatarUrl}
        size={compact ? 22 : 26}
        speaker={c.isSpeaker}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
            {c.author}
          </span>
          {c.isSpeaker && <SpeakerBadge label={labels.speaker} />}
          {c.time && (
            <span className="text-xs text-neutral-400 dark:text-neutral-500">
              {c.time}
            </span>
          )}
        </div>
        <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed mt-0.5 break-words">
          {c.text}
        </p>
        {(onReply || showLike) && (
          <div className="flex items-center gap-3 mt-1.5">
            {onReply && (
              <button
                type="button"
                onClick={() => onReply(c)}
                className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
              >
                {labels.reply}
              </button>
            )}
            {showLike && (
              <button
                type="button"
                aria-pressed={!!c.liked}
                aria-busy={likePending}
                disabled={likePending}
                onClick={() => onToggleLike!(c.id)}
                className={cn(
                  "flex items-center gap-1 text-xs transition-all",
                  c.liked
                    ? "text-neutral-900 dark:text-neutral-100"
                    : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100",
                  likePending && "opacity-50",
                )}
              >
                <ThumbsUp className={cn("size-3", c.liked && "fill-current")} />
                <span className="tabular-nums">{c.likes}</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CommentSheet({
  comments,
  reaction,
  reactionCounts,
  onReact,
  value,
  onChange,
  onSubmit,
  submitting,
  replyTo,
  onReplyChange,
  open,
  onOpenChange,
  disabled,
  isInView = true,
  onToggleLike,
  pendingLikeIds,
  labels: labelOverrides,
  className,
}: CommentSheetProps) {
  const { t } = useViewerLanguage();
  const labels = React.useMemo(
    () => ({ ...commentSheetLabels(t), ...labelOverrides }),
    [t, labelOverrides],
  );
  const reducedMotion = useReducedMotion();
  const dragControls = useDragControls();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const [previewIndex, setPreviewIndex] = React.useState(0);
  const [sort, setSort] = React.useState<"new" | "top">("new");

  const total = countAll(comments);
  const hasLikes =
    !!onToggleLike && comments.some((c) => typeof c.likes === "number");

  React.useEffect(() => {
    if (open || !isInView || reducedMotion || comments.length < 2) return;
    const timer = window.setInterval(
      () => setPreviewIndex((i) => (i + 1) % comments.length),
      3500,
    );
    return () => window.clearInterval(timer);
  }, [open, isInView, reducedMotion, comments.length]);

  React.useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  const adjustHeight = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  React.useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  const preview = comments.length
    ? comments[previewIndex % comments.length]
    : undefined;

  const authors = React.useMemo(() => {
    const seen = new Map<string, SheetComment>();
    for (const c of comments) if (!seen.has(c.author)) seen.set(c.author, c);
    return Array.from(seen.values()).slice(0, 3);
  }, [comments]);

  const sorted = React.useMemo(
    () =>
      sort === "top" && hasLikes
        ? [...comments].sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0))
        : comments,
    [comments, sort, hasLikes],
  );

  const hasValue = value.trim().length > 0;
  const showSend = !disabled && hasValue;
  const canSend = showSend && !submitting;

  return (
    <>
      <div
        onClick={() => onOpenChange(false)}
        {...stopScroll}
        className={cn(
          "absolute inset-0 z-20 bg-neutral-900/20 dark:bg-black/50 transition-opacity duration-300",
          open
            ? "opacity-100 pointer-events-auto backdrop-blur-sm"
            : "opacity-0 pointer-events-none",
        )}
        aria-hidden
      />

      <div
        {...stopScroll}
        className={cn(
          "absolute inset-x-0 bottom-0 z-10 bg-[var(--handyout-sheet)] border-t border-black/[0.06] dark:border-white/[0.08] px-3 py-2 flex items-center gap-2.5",
          className,
        )}
      >
        <button
          type="button"
          onClick={() => onOpenChange(true)}
          aria-expanded={open}
          className="flex-1 min-w-0 flex items-center gap-2.5 text-left"
        >
          {authors.length > 0 && (
            <div className="flex -space-x-1.5 shrink-0">
              {authors.map((a) => (
                <span
                  key={a.author}
                  className="rounded-full ring-1 ring-[var(--handyout-sheet)]"
                >
                  <Avatar
                    name={a.author}
                    url={a.avatarUrl}
                    size={22}
                    speaker={a.isSpeaker}
                  />
                </span>
              ))}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {total > 0 ? (
              <>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                  {labels.count(total)}
                </div>
                {preview && (
                  <div
                    key={`${preview.id}-${previewIndex}`}
                    className="text-sm truncate handyout-comment-preview-in"
                  >
                    <span className="font-medium text-neutral-900 dark:text-neutral-100">
                      {preview.author.split(" ")[0]}
                    </span>
                    <span className="text-neutral-500 dark:text-neutral-400">
                      {" "}
                      {preview.text}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <TextRevealRotate
                className="text-xs text-neutral-500 dark:text-neutral-400"
                texts={labels.emptyBarPhrases}
              />
            )}
          </div>
        </button>
        <ReactionPill
          reaction={reaction}
          counts={reactionCounts}
          onReact={onReact}
          disabled={disabled}
          labels={labels}
        />
      </div>

      <div
        {...stopScroll}
        role="dialog"
        aria-modal="true"
        aria-label={labels.title}
        aria-hidden={!open}
        className="absolute inset-x-0 bottom-0 z-30 flex flex-col h-[74%]"
        style={{
          transform: open ? "translateY(0)" : "translateY(102%)",
          // Hidden after the slide-out so closed inputs cannot be tabbed into.
          visibility: open ? "visible" : "hidden",
          transition: `transform 0.3s ease-out, visibility 0s linear ${open ? "0s" : "0.3s"}`,
        }}
      >
        <motion.div
          drag="y"
          dragListener={false}
          dragControls={dragControls}
          dragConstraints={CLOSE_DRAG_CONSTRAINTS}
          dragElastic={CLOSE_DRAG_ELASTIC}
          dragMomentum={false}
          dragTransition={reducedMotion ? SNAP_BACK_REDUCED : undefined}
          onDragEnd={(_, info) => {
            const flicked =
              info.offset.y > CLOSE_FLICK_MIN_OFFSET &&
              info.velocity.y > CLOSE_FLICK_VELOCITY;
            if (info.offset.y > CLOSE_DRAG_OFFSET || flicked) {
              onOpenChange(false);
            }
          }}
          className="flex flex-1 min-h-0 flex-col rounded-t-2xl bg-[var(--handyout-sheet)] ring-1 ring-black/[0.08] dark:ring-white/[0.12]"
        >
          <div
            onPointerDown={(e) => dragControls.start(e)}
            className="flex justify-center pt-2 pb-1 cursor-grab touch-none select-none active:cursor-grabbing"
          >
            <span className="h-1 w-9 rounded-full bg-black/[0.12] dark:bg-white/[0.18]" />
          </div>

          <div className="flex items-center justify-between gap-3 px-4 pb-3 pt-1">
            <div className="flex items-baseline gap-2">
              <span className="text-[15px] font-medium text-neutral-900 dark:text-neutral-100">
                {labels.title}
              </span>
              <span className="text-xs text-neutral-400 dark:text-neutral-500 tabular-nums">
                {total}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {hasLikes && (
                <div className="inline-flex items-center rounded-lg p-0.5 bg-neutral-100 dark:bg-neutral-900 ring-1 ring-black/[0.06] dark:ring-white/[0.08]">
                  {(["new", "top"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSort(s)}
                      className={cn(
                        "rounded-md px-2.5 py-1 text-xs transition-colors",
                        sort === s
                          ? "bg-white dark:bg-neutral-700 shadow-sm text-neutral-900 dark:text-neutral-100"
                          : "text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100",
                      )}
                    >
                      {s === "new" ? labels.sortNew : labels.sortTop}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                aria-label={labels.close}
                className="h-7 w-7 rounded-md flex items-center justify-center text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-black/[0.04] dark:hover:bg-white/[0.07]"
              >
                <X className="size-3.5" />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 border-t border-dashed border-black/[0.08] dark:border-white/[0.14]">
            {sorted.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {labels.emptySheet}
                </p>
              </div>
            ) : (
              sorted.map((c, i) => (
                <div
                  key={c.id}
                  className={cn(
                    "py-3.5",
                    i > 0 &&
                      "border-t border-dashed border-black/[0.06] dark:border-white/[0.11]",
                  )}
                >
                  <CommentItem
                    c={c}
                    labels={labels}
                    onToggleLike={onToggleLike}
                    pendingLikeIds={pendingLikeIds}
                    onReply={disabled ? undefined : (x) => onReplyChange(x)}
                  />
                  {c.replies && c.replies.length > 0 && (
                    <div className="ml-3 mt-3 pl-5 border-l border-black/[0.08] dark:border-white/[0.12] space-y-3">
                      {c.replies.map((r) => (
                        <CommentItem
                          key={r.id}
                          c={r}
                          compact
                          labels={labels}
                          onToggleLike={onToggleLike}
                          pendingLikeIds={pendingLikeIds}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="p-3 border-t border-black/[0.06] dark:border-white/[0.08] space-y-2">
            {replyTo && (
              <div className="flex items-start gap-2 rounded-md bg-neutral-100 dark:bg-neutral-900 ring-1 ring-black/[0.06] dark:ring-white/[0.08] px-2.5 py-1.5">
                <CornerUpLeft className="size-3.5 mt-0.5 text-neutral-400 dark:text-neutral-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    {labels.replyingTo(replyTo.author)}
                  </div>
                  <div className="text-xs text-neutral-900 dark:text-neutral-100 truncate">
                    {replyTo.text}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onReplyChange(undefined)}
                  aria-label={labels.cancelReply}
                  className="p-0.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
                >
                  <X className="size-3" />
                </button>
              </div>
            )}

            <div
              className={cn(
                "relative flex items-end rounded-lg ring-1 ring-black/[0.08] dark:ring-white/[0.1] focus-within:ring-black/[0.2] dark:focus-within:ring-white/[0.24] bg-white dark:bg-neutral-900 pl-3 pr-1.5 py-1.5 transition-shadow",
                disabled && "opacity-50",
              )}
            >
              {/* The button overlays the field rather than sitting beside it, so
                  its spring entrance cannot reflow the text mid-animation; the
                  padding below is what keeps the text clear of it. */}
              <motion.div
                animate={{ paddingRight: showSend ? 36 : 0 }}
                transition={SEND_SPRING}
                className="w-full min-w-0"
              >
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={value}
                  disabled={disabled}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && canSend) {
                      e.preventDefault();
                      onSubmit();
                    }
                  }}
                  placeholder={
                    replyTo
                      ? labels.replyPlaceholder(
                          replyTo.author.split(" ")[0] ?? replyTo.author,
                        )
                      : labels.placeholder
                  }
                  className="w-full min-w-0 block resize-none bg-transparent outline-none text-base pointer-fine:text-sm leading-5 py-1 min-h-7 max-h-28 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 disabled:cursor-not-allowed"
                />
              </motion.div>
              <AnimatePresence>
                {showSend && (
                  <motion.button
                    type="button"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={SEND_SPRING}
                    onClick={onSubmit}
                    disabled={!canSend}
                    aria-label={labels.send}
                    className="absolute right-1.5 bottom-1.5 h-7 w-7 rounded-md flex items-center justify-center shrink-0 bg-neutral-900 text-neutral-50 dark:bg-neutral-100 dark:text-neutral-900"
                  >
                    <ArrowUp className="size-3.5" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
